import { type CalendarDate, getLocalTimeZone, today } from '@internationalized/date';
import * as React from 'react';
import {
  Calendar as AriaCalendar,
  CalendarContext as AriaCalendarContext,
  CalendarGrid as AriaCalendarGrid,
  CalendarGridBody as AriaCalendarGridBody,
  CalendarGridHeader as AriaCalendarGridHeader,
  CalendarHeaderCell as AriaCalendarHeaderCell,
  CalendarStateContext as AriaCalendarStateContext,
  Heading as AriaHeading,
  useSlottedContext,
} from 'react-aria-components';

import { ChevronLeftIcon, ChevronRightIcon } from '../../icons';
import { cx } from '../../utils/cx';
import Button from '../Button/Button';

import { CalendarCell } from './Cell';
import { DateInput } from './DateInput';

import type { HTMLAttributes, PropsWithChildren } from 'react';
import type { CalendarProps as AriaCalendarProps, DateValue } from 'react-aria-components';

export const CalendarContextProvider = ({ children }: PropsWithChildren) => {
  const [value, onChange] = React.useState<DateValue | null>(null);
  const [focusedValue, onFocusChange] = React.useState<DateValue | undefined>();

  return (
    <AriaCalendarContext.Provider value={{ value, onChange, focusedValue, onFocusChange }}>
      {children}
    </AriaCalendarContext.Provider>
  );
};

const PresetButton = ({ value, children, ...props }: HTMLAttributes<HTMLButtonElement> & { value: CalendarDate }) => {
  const context = React.useContext(AriaCalendarStateContext);

  if (!context) {
    throw new Error('Preset must be used within a Calendar component');
  }

  const handleClick = () => {
    context.setValue(value);
    context.setFocusedDate(value);
  };

  return (
    <Button
      {...props}
      // It's important to give `null` explicitly to the `slot` prop
      // otherwise the button will throw an error due to not using one of
      // the required slots inside the Calendar component.
      // Passing `null` will tell the button to not use a slot context.
      slot={null}
      size="md"
      color="secondary"
      onClick={handleClick}
    >
      {children}
    </Button>
  );
};

interface ICalendarProps extends AriaCalendarProps<DateValue> {
  /** The dates to highlight. */
  highlightedDates?: DateValue[];
}

export const Calendar = ({ highlightedDates, className, ...props }: ICalendarProps) => {
  const context = useSlottedContext(AriaCalendarContext)!;

  const ContextWrapper = context ? React.Fragment : CalendarContextProvider;

  return (
    <ContextWrapper>
      <AriaCalendar
        {...props}
        className={(state) => cx('flex flex-col gap-3', typeof className === 'function' ? className(state) : className)}
      >
        <header className="flex items-center justify-between">
          <Button slot="previous" iconLeading={ChevronLeftIcon} size="sm" color="tertiary" className="size-8" />
          <AriaHeading className="text-fg-secondary text-sm font-semibold" />
          <Button slot="next" iconLeading={ChevronRightIcon} size="sm" color="tertiary" className="size-8" />
        </header>

        <div className="flex gap-3">
          <DateInput className="flex-1" />
          <PresetButton value={today(getLocalTimeZone())}>Today</PresetButton>
        </div>

        <AriaCalendarGrid weekdayStyle="short" className="w-max">
          <AriaCalendarGridHeader className="border-b-4 border-transparent">
            {(day) => (
              <AriaCalendarHeaderCell className="p-0">
                <div className="text-secondary flex size-10 items-center justify-center text-sm font-medium">
                  {day.slice(0, 2)}
                </div>
              </AriaCalendarHeaderCell>
            )}
          </AriaCalendarGridHeader>
          <AriaCalendarGridBody className="[&_td]:p-0 [&_tr:last-of-type]:border-none [&_tr]:border-b-4 [&_tr]:border-transparent">
            {(date) => (
              <CalendarCell
                date={date}
                isHighlighted={highlightedDates?.some((highlightedDate) => date.compare(highlightedDate) === 0)}
              />
            )}
          </AriaCalendarGridBody>
        </AriaCalendarGrid>
      </AriaCalendar>
    </ContextWrapper>
  );
};
